import moment from 'moment-timezone';
import { DashboardView } from '../view/DashboardView';
import { templateDashboard } from '../view/template';
import { DashboardRepository } from '../repository/DashboardRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../framework/BaseWebComponentController';
import { DashboardWebComponent } from '..';
import { CanExportPdf, Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { Dashboard } from '../../types';
import { GzDropdownWebComponent } from '../../ui/gzDropdown';
import { downloadBlob } from '../../utils/files.utils';
import { SelectEvents } from '../../ui/gzSelect/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { getFormattedDateTime, TimestampFormat } from '@nirvana/shared/time';

export class DashboardController extends BaseWebComponentController {
  public component: DashboardWebComponent;
  public dataRepository: DashboardRepository;
  public view: DashboardView;
  public viewConstructor: typeof DashboardView;
  public dataRepositoryConstructor: typeof DashboardRepository;
  private loadingCount = 0;

  constructor(component: DashboardWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: DashboardView,
      viewTemplate: templateDashboard,
      dataRepositoryConstructor: DashboardRepository,
    });
  }

  computeEventListeners() {
    this.view.dom.$companySelectorDropdown.selectedTarget = this.component.initialSelectedTarget;
    this.view.dom.$companySelectorDropdown.availableCompanies = this.component.initialAvailableCompanies;
    this.view.dom.$companySelectorDropdown.onCompanyClicked = this.onCompanyClicked.bind(this);
    this.view.dom.$companySelectorDropdown.onCompanyChangedWithSuccess = this.onCompanyChangedWithSuccess.bind(this);
    this.view.dom.$companySelectorDropdown.onCompanyChangedWithError = this.onCompanyChangedWithError.bind(this);

    return [
      {
        element: this.view.dom.$timeRangeDropdown,
        event: 'selectionChanged',
        callback: this.onTimeRangeChanged,
      },
      {
        element: this.view.dom.$wrapper,
        event: 'dataLoadingFinished',
        callback: this.onDataLoadingFinished,
      },
      {
        element: this.view.dom.$dashboardActions,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.executiveSummaryAction,
      },
    ];
  }

  async onTimeRangeChanged(ev: WebComponentEvent) {
    const lastNumDays = ev.detail.value;
    const selector = ev.target as GzDropdownWebComponent;

    if (!this.component.timeRange) {
      throw new Error(`Time range changed but expected time range to be already set!`);
    }

    /**
     * Save the current timeRange value, so we can reset it in the dropdown if we get an error.
     */
    let prevTimeRange;
    if (this.component.timeRange.kind === TimeRangeKind.RELATIVE_TIME_RANGE) {
      prevTimeRange = this.component.timeRange.relative?.lastNumDays.toString();
    }

    const timeRange = {
      kind: TimeRangeKind.RELATIVE_TIME_RANGE,
      relative: { lastNumDays: parseInt(lastNumDays, 10) },
    };

    this.view.setDisableForSelectorsAndButtons(true);

    try {
      // Set the selected time range to the backend.
      await this.dataRepository.setTimeRange({
        dashboardId: this.component.dashboardId,
        timeRange: timeRange,
      });
      this.component.timeRange = timeRange;
    } catch (e) {
      console.error(e);
      if (prevTimeRange) {
        selector.value = prevTimeRange;
      }
      this.view.setDisableForSelectorsAndButtons(false);
      this.view.buildTimeRangeSelectorErrorDialog();
    }

    // Then, re-render. This will set the time range for all the widgets,
    // which in turn will trigger data updates for the given range.
    await this.render();
    return false;
  }

  private async onCompanyClicked(clickedCompany: Target) {
    this.view.setLoadingState();
    this.view.setDisableForSelectorsAndButtons(true);

    const data = await this.dataRepository.getWidgetsForDashboard({ dashboardId: this.component.dashboardId });

    await this.dataRepository.setTargets({
      dashboardId: this.component.dashboardId,
      targets: [clickedCompany.companyId],
    });

    if (data) {
      this.component.widgets = data.widgets;
      this.component.canDrillDown = data.canDrillDown || false;
    }
  }

  private async onCompanyChangedWithSuccess() {
    this.view.removeLoadingState();
    await this.render();
  }

  private async onCompanyChangedWithError() {
    this.view.setDisableForSelectorsAndButtons(false);
    this.view.removeLoadingState();
    await this.render();
  }

  onDataLoadingFinished() {
    // increment the loading count for each 'dataLoadingFinished' event
    this.loadingCount++;

    // when the count === widgets count, enable the selectors and other buttons.
    if (this.loadingCount === this.component.widgets.length) {
      this.loadingCount = 0;
      this.view.setDisableForSelectorsAndButtons(false);
    }
  }

  private executiveSummaryAction(ev: WebComponentEvent) {
    switch (ev.detail.selected) {
      case 0:
        return this.exportPdf();
      case 1:
        return this.showReportModal();
    }
  }

  private async exportPdf() {
    if (this.view.canExportPdf === CanExportPdf.VISIBLE_BUT_DISABLED) {
      this.view.showExportPdfError(
        this.dependencies.translateFn('The export functionality is temporarily disabled. Please try again later.')
      );
      return;
    }

    this.view.setDisableForSelectorsAndButtons(true);
    this.view.dom.$actionsButton.showSpinner = true;

    try {
      const widgetResults = this.view.getWidgetResultsByWidgetId();

      if (!widgetResults.allWidgetsWithResult) {
        this.view.showExportPdfError(
          this.dependencies.translateFn('A widget error prevented the PDF export. Please try again later.')
        );
        return;
      }

      if (!this.component.timeRange) {
        throw new Error(`Programmatic error - expected to have time range when exporting PDF`);
      }

      const exportPdfRequest = {
        dashboard: {
          id: this.component.dashboardId,
          name: this.component.dashboardTitle,
          type: this.component.dashboardType,
          timeRange: this.component.timeRange,
          widgets: this.component.widgets,
          targets: [this.view.dom.$companySelectorDropdown.selectedTarget.companyId],
        },
        dashboardToken: '',
        widgetsResult: (this.component.widgets || []).map(widget => {
          const result = widgetResults.results[widget.id];
          return {
            widgetId: widget.id,
            widgetResultJson: JSON.stringify(result),
          };
        }),
        locale: this.dependencies.getUserLanguage(),
        timezone: this.dependencies.getTimezone(),
        reportingPeriod: this.view.reportingInterval,
        selectedTarget: this.view.dom.$companySelectorDropdown.selectedTarget,
      };

      try {
        const pdfResponse = await this.dataRepository.getExecutiveSummaryPdf(exportPdfRequest);
        const fileBlob = new Blob([pdfResponse.pdf], { type: 'application/pdf' });
        const reportTime = getFormattedDateTime(
          TimestampFormat.FILES,
          this.view.reportingInterval.current.toTimestamp ?? Date.now(),
          this.dependencies.getTimezone()
        );
        const execPdfName = `Executive Summary ${this.dependencies.getUserLanguage()} ${reportTime}.pdf`;
        downloadBlob(fileBlob, execPdfName);
      } catch (error) {
        if (error.data && error.data.details && error.data.details.includes('Not Authenticated')) {
          return; // Not authenticated errors are handled on client level
        }
        console.error(error);
        this.view.showExportPdfError(
          this.dependencies.translateFn(
            'An internal error occurred. Please try again later or contact Bitdefender Enterprise Support if the issue persists.'
          )
        );
      }
    } finally {
      this.view.dom.$actionsButton.showSpinner = false;
      this.view.setDisableForSelectorsAndButtons(false);
    }
  }

  private showReportModal() {
    const timeRange = this.component.timeRange;
    const selectedTarget = this.view.dom.$companySelectorDropdown.selectedTarget;
    if (!timeRange) {
      throw new Error('Unexpectedly missing time range when opening the report modal');
    }
    NavigationHelper.showReportForm(this.dependencies.Ext, timeRange, selectedTarget);
  }

  async getDataForView(): Promise<Dashboard> {
    return {
      id: this.component.dashboardId,
      type: this.component.dashboardType,
      timeRange: this.component.timeRange,
      reportingPeriod: this.component.reportingPeriod,
      title: this.component.dashboardTitle,
      widgets: this.component.widgets || [],
      selectedTarget: this.view.dom.$companySelectorDropdown.selectedTarget,
      isPartner: this.component.isPartner,
      canDrillDown: this.component.canDrillDown,
      features: this.component.features,
      displayMode: this.component.displayMode,
      hasReportsEnabled: this.component.hasReportsEnabled,
    };
  }
}
